import { ActionTree } from 'vuex';
import i18n from '@/i18n';
import { Notification } from 'uiv';
import { cloneDeep, uniqueId } from 'lodash';
import httpClient from '@/httpClient/httpClient';
import {
  ResponseBody,
  RootState,
  ResponseDataProvider,
  Country,
  IChipsData,
  typeTraffic,
  ResponseData,
} from '@/types';
import {
  Content,
  InventoryState,
  IStateInventory,
  IWidgetParams,
  Placement,
  TInventoryData,
  TNewData,
  TNewDataWithId,
  TRequestData,
  TSubmitData,
} from '@/types/publisher/inventory';
import ResponseDataValue, {
  ResponseDataProviderValue,
} from '@/utils/ResponseDataValue';
import {
  createWidgetParams,
  createBannerWidgetParams,
  createNativeWidgetParams,
  createPushWidgetParams,
} from '@/store/utils/createPlacementWidgetParams';
import {
  setPublisherErrorsFromResponseData,
  validateInventoryForm,
  validatePlacementsForm,
  validatePlacementWidgetParams,
} from '@/store/utils/validationForms';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');
const DELETE_SUCCESS = i18n.t('notifications.deleteSuccess');
const HOME_PAGE = '/publisher/inventory';
const PREFIX_ID = 'new-';

function createNewId(): string {
  return uniqueId(PREFIX_ID);
}

const actions: ActionTree<InventoryState, RootState> = {
  setInventory({ commit, getters }, data): void {
    const inventory: IStateInventory = cloneDeep(
      getters.getInventoryInitialState
    );
    inventory.id = data.id.toString();
    inventory.name = data.name ?? inventory.name;
    inventory.domain = data.domain ?? inventory.domain;
    inventory.status = data.status.value ?? inventory.status;
    inventory.languages =
      data.language_code?.map((l: Country): string => l.code) ??
      inventory.languages;
    inventory.categories =
      data.categories?.map((c: IChipsData): IChipsData => c) ??
      inventory.categories;
    inventory.placements =
      data.placements?.map((p: Placement): string => p.id.toString()) ??
      inventory.placements;

    commit('setInventory', inventory);
  },
  setPlacements({ commit, getters }, data): void {
    for (const placement of data.placements) {
      const placementId: string = placement.id.toString();
      const newPlacement = cloneDeep(getters.getPlacementInitialState);

      newPlacement.id = placementId ?? newPlacement.id;
      newPlacement.name = placement.name ?? newPlacement.name;
      newPlacement.type = placement.type?.value ?? newPlacement.type;
      newPlacement.emptySlotType =
        placement.empty_slot_type ?? newPlacement.emptySlotType;
      if (placement.unwanted_contents?.categories?.length) {
        newPlacement.unwantedCategories =
          placement.unwanted_contents?.categories.map((uc: IChipsData) => uc);
      }
      if (placement.unwanted_contents?.links?.length) {
        newPlacement.unwantedLinks = placement.unwanted_contents?.links;
      }
      newPlacement.showContentAddButton = true;
      newPlacement.showUnwantedContentAddButton = true;

      commit('addPlacement', newPlacement);
    }
  },
  setPlacementContents({ commit }, data): void {
    for (const placement of data.placements) {
      const placementId: string = placement.id.toString();

      if (placement.contents?.length) {
        for (const content of placement.contents) {
          if (!content.img) continue;

          const contentId: string = content.id.toString();
          const newContent: Content = {
            id: contentId,
            img: content.img,
            title: content.title ?? '',
            description: content.description ?? '',
            link: content.link ?? '',
            creative: {
              img: content.img,
            },
            isChoosedImage: false,
            isEdited: false,
          };

          commit('addContent', [placementId, newContent]);
        }
      }
    }
  },
  setPlacementWidgetParams({ commit, getters }, data): void {
    for (const placement of data.placements) {
      const placementId: string = placement.id.toString();
      let newWidgetParams: IWidgetParams = cloneDeep(
        getters.getWidgetParamsInitialState
      );
      newWidgetParams.type = placement.type.value;

      if (
        placement.widget_params &&
        Object.keys(placement.widget_params).length
      ) {
        newWidgetParams = createWidgetParams(
          placement.widget_params,
          newWidgetParams
        );
      }

      commit('addWidgetParams', [placementId, newWidgetParams]);
    }
  },

  addInventory({ commit, getters }): void {
    const newInventory: IStateInventory = cloneDeep(
      getters.getInventoryInitialState
    );
    newInventory.id = createNewId();

    commit('addInventory', newInventory);
  },

  addPlacement({ commit, getters }): void {
    const newPlacement: Placement = cloneDeep(getters.getPlacementInitialState);
    const placementId: string = createNewId();
    newPlacement.id = placementId;

    const widgetParams: IWidgetParams = cloneDeep(
      getters.getWidgetParamsInitialState
    );
    const payload = [placementId, widgetParams];

    commit('addPlacement', newPlacement);
    commit('addWidgetParams', payload);
  },
  addPlacementContentData({ commit }, payload) {
    const [placementId, newContent] = payload;
    newContent.id = createNewId();
    commit('addContent', [placementId, newContent]);
  },

  updateInventoryData({ commit }, newData: TNewData): void {
    commit('updateInventory', newData);
  },
  updatePlacementData({ commit }, newDataWithId: TNewDataWithId): void {
    commit('updatePlacement', newDataWithId);
  },
  updatePlacementWidgetParamsData(
    { commit },
    newDataWithId: TNewDataWithId
  ): void {
    commit('updateWidgetParams', newDataWithId);
  },
  updatePlacementContentData({ commit }, newContent): void {
    commit('updateContents', newContent);
  },

  deletePlacementData({ commit }, placementId: string) {
    commit('deletePlacement', placementId);
  },
  deletePlacementContentData({ commit }, payload) {
    commit('deleteContent', payload);
  },
  clearAllState({ commit }) {
    commit('clearAllState');
  },

  // SUBMIT FORM
  async submitForm({ commit, getters, dispatch }, submitData: TSubmitData) {
    const inventory = getters.getInventory;
    const placements = getters.getPlacements;
    const placementWidgetParams = getters.getPlacementWidgetParams;
    let isValidInventory = true;
    let isValidplacements = true;
    let isValidWidgetPatams = true;

    isValidInventory = validateInventoryForm(inventory);

    for (const id of placements.allIds) {
      const placement = placements.byId[id];
      const widgetParams = placementWidgetParams.byId[id];

      if (
        !placement.name &&
        !widgetParams.element &&
        !widgetParams.width &&
        !widgetParams.height
      ) {
        dispatch('deletePlacementData', id);
        continue;
      }

      if (
        widgetParams.element !== '' ||
        widgetParams.width !== '' ||
        widgetParams.height !== ''
      ) {
        isValidplacements = validatePlacementsForm(placement);
      }

      if (
        placement.name ||
        widgetParams.element ||
        widgetParams.width ||
        widgetParams.height
      ) {
        isValidWidgetPatams = validatePlacementWidgetParams(widgetParams);
      }
    }

    if (!isValidInventory || !isValidplacements || !isValidWidgetPatams) {
      Notification.notify.warning({
        title: i18n.t('notifications.checkForm'),
        duration: 3000,
      });
      return;
    }

    commit('changeIsSavingForm', true);
    const formData = new FormData();

    // Inventory
    if (inventory.id && !inventory.id.includes(PREFIX_ID)) {
      formData.append('id', inventory.id);
    }
    if (inventory.name) {
      formData.append('name', inventory.name);
    }
    if (inventory.domain) {
      formData.append('domain', inventory.domain);
    }
    if (inventory.status) {
      formData.append('status', inventory.status);
    } else {
      formData.append('status', 'draft');
    }
    if (inventory.languages?.length) {
      for (const lang of inventory.languages) {
        if (lang) {
          formData.append('language_code[]', lang);
        }
      }
    } else {
      formData.append('language_code[]', '[]');
    }
    if (inventory.categories?.length) {
      for (const category of inventory.categories) {
        if (category) {
          formData.append('categories[]', category.value.toString());
        }
      }
    } else {
      formData.append('categories[]', '[]');
    }

    // Placements
    if (placements?.allIds?.length) {
      placements.allIds.forEach((placementId: string, index: number): void => {
        const placement = placements.byId[placementId];
        const placementsIndex = `placements[${index}]`;

        if (placement.id && !placement.id.includes(PREFIX_ID)) {
          formData.append(`${placementsIndex}[id]`, placement.id);
        }
        if (placement.name) {
          formData.append(`${placementsIndex}[name]`, placement.name);
        }
        if (placement.type) {
          formData.append(`${placementsIndex}[type]`, placement.type);
        }
        if (placement.emptySlotType) {
          formData.append(
            `${placementsIndex}[empty_slot_type]`,
            placement.emptySlotType
          );
        }
        if (placement.unwantedCategories?.length) {
          for (const category of placement.unwantedCategories) {
            formData.append(
              `${placementsIndex}[unwanted_contents][categories][]`,
              category.value.toString()
            );
          }
        } else {
          formData.append(
            `${placementsIndex}[unwanted_contents][categories][]`,
            '[]'
          );
        }

        if (placement.unwantedLinks?.length) {
          for (const link of placement.unwantedLinks) {
            formData.append(
              `${placementsIndex}[unwanted_contents][links][]`,
              link
            );
          }
        } else {
          formData.append(
            `${placementsIndex}[unwanted_contents][links][]`,
            '[]'
          );
        }

        // Placement contents
        const contents = getters.getPlacementContentsById(placementId);

        if (contents?.length) {
          contents.forEach((content: Content, idx: number) => {
            const contentIndex = `${placementsIndex}[contents][${idx}]`;

            if (content.id && !content.id.includes(PREFIX_ID)) {
              formData.append(`${contentIndex}[id]`, content.id);
            }
            if (content.title) {
              formData.append(`${contentIndex}[title]`, content.title);
            }
            if (content.description) {
              formData.append(
                `${contentIndex}[description]`,
                content.description
              );
            }
            if (content.link) {
              formData.append(`${contentIndex}[link]`, content.link);
            }
            if (content.creative?.file) {
              formData.append(`${contentIndex}[file]`, content.creative.file);
            }
          });
        } else {
          formData.append(`${placementsIndex}[contents][]`, '[]');
        }

        // Placement widget params
        const widgetParams = placementWidgetParams.byId[placementId];
        if (placement.type === typeTraffic.BANNER) {
          const bannerWidgetParams = createBannerWidgetParams(widgetParams);
          formData.append(
            `${placementsIndex}[widget_params]`,
            JSON.stringify(bannerWidgetParams)
          );
        } else if (placement.type === typeTraffic.NATIVE) {
          const nativeWidgetParams = createNativeWidgetParams(widgetParams);
          formData.append(
            `${placementsIndex}[widget_params]`,
            JSON.stringify(nativeWidgetParams)
          );
        } else if (placement.type === typeTraffic.PUSH) {
          const pushWidgetParams = createPushWidgetParams(widgetParams);
          formData.append(
            `${placementsIndex}[widget_params]`,
            JSON.stringify(pushWidgetParams)
          );
        }
      });
    } else {
      formData.append('placements[]', '[]');
    }

    const { APIName, inventoryId } = submitData;
    const requestData: TRequestData = {
      formData,
    };
    if (inventoryId) {
      requestData.id = inventoryId;
    }
    const response: ResponseData = await dispatch(APIName, requestData);

    if (response.success) {
      window.location.href = HOME_PAGE;
    } else {
      const errors = response.data;
      const inventoryData: TInventoryData = {
        inventory,
        placements,
        placementWidgetParams,
      };

      setPublisherErrorsFromResponseData(errors, inventoryData);
    }
    commit('changeIsSavingForm', false);
  },

  // ******************************
  // API
  async inventory({ dispatch }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/publisher/' + data.id,
        {
          params: data,
        }
      );
      dispatch('setInventory', response.data);
      dispatch('setPlacements', response.data);
      dispatch('setPlacementContents', response.data);
      dispatch('setPlacementWidgetParams', response.data);
      return ResponseDataValue(response.data, true);
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async createInventory({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/publisher',
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
  async updateInventory({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/publisher/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
  async changeInventoryStatus({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/publisher/change-status/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
  async deleteInventory({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().delete(
        '/publisher/' + data.id
      );
      commit('deleteInventory', data.id);
      Notification.notify.success({
        title: DELETE_SUCCESS,
      });
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
  async inventoryList({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/publisher', {
        params: Object.assign({}, data.searchData, {
          expand: 'categories,languages,placements',
          fields: data.columns.join(','),
        }),
      });
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);
      commit('setInventoryListFromResponse', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async changePlacementStatus({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().put(
        '/publisher/placement/change-status/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
  async createPlacement({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().post(
        '/publisher/placement/' + data.id,
        data.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
  async deletePlacement({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().delete(
        '/publisher/placement/' + data.placementId
      );
      commit('deletePlacementFromInventoryList', data);
      Notification.notify.success({
        title: DELETE_SUCCESS,
      });
      return ResponseDataValue(response.data, true);
    } catch (e) {
      return ResponseDataValue(e.response.data, false);
    }
  },
};

export default actions;
